import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Clock, RefreshCw, XCircle, CheckCircle2, AlertTriangle, MailX, Search, Ban, Plus } from "lucide-react";

const API = "/api";

function HoldCountdown({ releaseAt, holdWindowSeconds = 120 }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const release = new Date(releaseAt).getTime();
  const remaining = Math.max(0, Math.floor((release - now) / 1000));
  const pct = Math.max(0, Math.min(100, (remaining / Math.max(1, holdWindowSeconds)) * 100));
  const tone = remaining < 30 ? "text-[#A4262C]" : remaining < 60 ? "text-[#D83B01]" : "text-[#0F6CBD]";
  return (
    <div className="flex items-center gap-2" data-testid="hold-countdown">
      <Clock size={13} className={tone} />
      <span className={`font-mono text-xs tabular-nums ${tone}`}>{remaining}s</span>
      <div className="h-1 w-16 bg-[#E1DFDD] rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-1000 ${remaining < 30 ? "bg-[#A4262C]" : "bg-[#0F6CBD]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function statusBadge(status) {
  const map = {
    released: { label: "Delivered", color: "bg-[#DFF6DD] text-[#107C10]", icon: CheckCircle2 },
    recalled: { label: "Recalled", color: "bg-[#FED9CC] text-[#A4262C]", icon: XCircle },
    release_failed: { label: "Failed", color: "bg-[#FFF4CE] text-[#8A6116]", icon: AlertTriangle },
    release_retrying: { label: "Retrying", color: "bg-[#FFF4CE] text-[#8A6116]", icon: RefreshCw },
    cancelled: { label: "Cancelled", color: "bg-[#FED9CC] text-[#A4262C]", icon: XCircle },
  };
  const m = map[status] || { label: status, color: "bg-[#F3F2F1] text-[#5E5E5E]", icon: Clock };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${m.color}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

function nextRetryHint(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = Math.max(0, target - Date.now());
  if (diff === 0) return "any moment";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ~${mins} min`;
  const hrs = Math.round(diff / 3600000);
  return `in ~${hrs} hour${hrs === 1 ? "" : "s"}`;
}

export default function Outbox() {
  const [tab, setTab] = useState("held"); // held | recent
  const [held, setHeld] = useState([]);
  const [recent, setRecent] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [holdWindow, setHoldWindow] = useState(120);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [confirm, setConfirm] = useState(null); // { hold_id, subject, mail_from }
  const [recalling, setRecalling] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [newBlock, setNewBlock] = useState("");
  const [blocking, setBlocking] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/relay/held`).catch(() => ({ data: { held_emails: [] } })),
      axios.get(`${API}/relay/recent`, { params: { limit: 30 } }).catch(() => ({ data: { emails: [] } })),
      axios.get(`${API}/relay/status`).catch(() => ({ data: {} })),
      axios.get(`${API}/relay/suppressions`).catch(() => ({ data: { suppressions: [] } })),
    ]).then(([h, r, s, sup]) => {
      setHeld(h.data.held_emails || []);
      setRecent(r.data.emails || []);
      setSuppressions(sup.data.suppressions || []);
      if (s.data?.hold_seconds) setHoldWindow(s.data.hold_seconds);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 5000); // 5s live refresh on held queue
    return () => clearInterval(i);
  }, [refresh]);

  const doRecall = async () => {
    if (!confirm) return;
    setRecalling(true);
    try {
      const r = await axios.post(`${API}/relay/recall`, {
        hold_id: confirm.hold_id,
        reason: "dashboard_outbox",
      });
      if (r.data?.success) {
        toast.success(`Recalled "${confirm.subject || "(no subject)"}"`);
        setConfirm(null);
        refresh();
      } else {
        toast.error(r.data?.error || "Recall failed — email may have already been released");
      }
    } catch (e) {
      toast.error("Recall failed: " + (e.response?.data?.error || e.message));
    }
    setRecalling(false);
  };

  const doRetry = async (row) => {
    setRetryingId(row.hold_id);
    try {
      const r = await axios.post(`${API}/relay/retry`, { hold_id: row.hold_id });
      if (r.data?.success) {
        toast.success(`Retry delivered "${row.subject || "(no subject)"}"`);
      } else if (r.data?.skipped === "already_delivered") {
        toast.info("Already delivered — no retry needed.");
      } else {
        const code = r.data?.error_code ? `[${r.data.error_code}] ` : "";
        toast.error(`Retry still failing — ${code}${r.data?.error || "see Outbox for details"}`);
      }
      refresh();
    } catch (e) {
      toast.error("Retry failed: " + (e.response?.data?.error || e.message));
    }
    setRetryingId(null);
  };

  const doCancel = async (row) => {
    setCancellingId(row.hold_id);
    try {
      const r = await axios.post(`${API}/relay/cancel-retry`, { hold_id: row.hold_id });
      if (r.data?.success) {
        toast.success(`Cancelled "${row.subject || "(no subject)"}" — removed from retry queue`);
      } else if (r.data?.skipped === "already_delivered") {
        toast.info("Already delivered — nothing to cancel.");
      } else {
        toast.error("Cancel failed: " + (r.data?.error || "see Outbox for details"));
      }
      refresh();
    } catch (e) {
      toast.error("Cancel failed: " + (e.response?.data?.error || e.message));
    }
    setCancellingId(null);
  };

  const doUnblock = async (recipient) => {
    try {
      const r = await axios.post(`${API}/relay/suppressions/remove`, { recipient });
      if (r.data?.success) {
        toast.success(`Unblocked ${recipient} — delivery will resume`);
      } else {
        toast.error("Unblock failed: " + (r.data?.error || "unknown error"));
      }
      refresh();
    } catch (e) {
      toast.error("Unblock failed: " + (e.response?.data?.error || e.message));
    }
  };

  const doBlock = async () => {
    const recipient = newBlock.trim().toLowerCase();
    if (!recipient || !recipient.includes("@")) {
      toast.error("Enter a valid email address to block");
      return;
    }
    setBlocking(true);
    try {
      const r = await axios.post(`${API}/relay/suppressions`, { recipient });
      if (r.data?.success) {
        toast.success(`Blocked ${recipient} — future emails to it will be dropped`);
        setNewBlock("");
      } else {
        toast.error("Block failed: " + (r.data?.error || "unknown error"));
      }
      refresh();
    } catch (e) {
      toast.error("Block failed: " + (e.response?.data?.error || e.message));
    }
    setBlocking(false);
  };

  const filterFn = (e) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (e.mail_from || "").toLowerCase().includes(f) ||
      (e.subject || "").toLowerCase().includes(f) ||
      (e.rcpt_tos || []).some((r) => r.toLowerCase().includes(f))
    );
  };

  const heldFiltered = held.filter(filterFn);
  const recentFiltered = recent.filter(filterFn);
  const stuck = recent.filter((e) => e.status === "release_retrying");

  return (
    <div className="p-8 max-w-6xl" data-testid="outbox-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[#171717]">Outbox</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">
            Manage the 120-second hold queue. Recall an email before it leaves the relay.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="outbox-refresh-btn"
        >
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stuck-in-retry panel — only shows when emails are looping in the queue */}
      <StuckRetryPanel rows={stuck} onCancel={doCancel} cancellingId={cancellingId} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E1DFDD] mb-4" data-testid="outbox-tabs">
        <button
          onClick={() => setTab("held")}
          data-testid="tab-held"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "held"
              ? "border-[#0F6CBD] text-[#0F6CBD]"
              : "border-transparent text-[#5E5E5E] hover:text-[#171717]"
          }`}
        >
          Held now
          <span className="ml-2 bg-[#FFF4CE] text-[#8A6116] px-1.5 py-0.5 rounded text-[10px] font-semibold" data-testid="held-count">
            {held.length}
          </span>
        </button>
        <button
          onClick={() => setTab("recent")}
          data-testid="tab-recent"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "recent"
              ? "border-[#0F6CBD] text-[#0F6CBD]"
              : "border-transparent text-[#5E5E5E] hover:text-[#171717]"
          }`}
        >
          Recent activity
          <span className="ml-2 bg-[#F3F2F1] text-[#5E5E5E] px-1.5 py-0.5 rounded text-[10px] font-semibold">
            {recent.length}
          </span>
        </button>
        <button
          onClick={() => setTab("suppressed")}
          data-testid="tab-suppressed"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "suppressed"
              ? "border-[#0F6CBD] text-[#0F6CBD]"
              : "border-transparent text-[#5E5E5E] hover:text-[#171717]"
          }`}
        >
          Blocklist
          <span className="ml-2 bg-[#FED9CC] text-[#A4262C] px-1.5 py-0.5 rounded text-[10px] font-semibold" data-testid="suppressed-count">
            {suppressions.length}
          </span>
        </button>
      </div>

      {/* Filter */}
      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]" />
        <Input
          placeholder="Filter by sender, recipient or subject…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9 text-sm"
          data-testid="outbox-filter"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E1DFDD] rounded-md overflow-hidden">
        {tab === "held" ? (
          <HeldTable rows={heldFiltered} onRecall={setConfirm} loading={loading} holdWindow={holdWindow} />
        ) : tab === "recent" ? (
          <RecentTable rows={recentFiltered} loading={loading} onRetry={doRetry} retryingId={retryingId} onCancel={doCancel} cancellingId={cancellingId} />
        ) : (
          <SuppressedTable
            rows={suppressions}
            loading={loading}
            onUnblock={doUnblock}
            onBlock={doBlock}
            newBlock={newBlock}
            setNewBlock={setNewBlock}
            blocking={blocking}
          />
        )}
      </div>

      {/* Recall confirmation modal */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="recall-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Recall this email?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">The email will be permanently cancelled and never delivered to its recipients.</span>
              {confirm && (
                <span className="block mt-3 bg-[#F3F2F1] p-3 rounded border border-[#E1DFDD] text-xs">
                  <strong>From:</strong> {confirm.mail_from}<br />
                  <strong>To:</strong> {(confirm.rcpt_tos || []).join(", ")}<br />
                  <strong>Subject:</strong> {confirm.subject || "(no subject)"}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recall-cancel-btn">Keep delivering</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRecall}
              disabled={recalling}
              data-testid="recall-confirm-btn"
              className="bg-[#A4262C] hover:bg-[#8E1F24]"
            >
              {recalling ? "Recalling…" : "Recall email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SuppressedTable({ rows, loading, onUnblock, onBlock, newBlock, setNewBlock, blocking }) {
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  return (
    <div data-testid="suppressed-section">
      {/* Manual block bar */}
      <div className="flex items-center gap-2 border-b border-[#E1DFDD] bg-[#FAFAFA] px-4 py-3">
        <Ban size={15} className="text-[#A4262C]" />
        <span className="text-xs text-[#5E5E5E] mr-1">
          Addresses that repeatedly hard-fail are auto-blocked. Mail to them is dropped (never bounces, never floods the queue).
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="block an address (e.g. info@kcl.kr)"
            value={newBlock}
            onChange={(e) => setNewBlock(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onBlock()}
            className="h-8 w-64 text-sm"
            data-testid="block-recipient-input"
          />
          <Button
            size="sm"
            onClick={onBlock}
            disabled={blocking}
            data-testid="block-recipient-btn"
            className="h-8 bg-[#A4262C] hover:bg-[#8E1F24]"
          >
            <Plus size={13} className="mr-1" />
            {blocking ? "Blocking…" : "Block"}
          </Button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#5E5E5E]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-12 text-center" data-testid="suppressed-empty">
          <Ban size={32} className="mx-auto text-[#5E5E5E] mb-3" strokeWidth={1.4} />
          <p className="text-sm text-[#5E5E5E]">No blocked recipients.</p>
          <p className="text-xs text-[#5E5E5E] mt-1">Dead or looping addresses will be auto-added here to protect the relay.</p>
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="suppressed-table">
          <thead className="bg-[#FAFAFA] text-[#5E5E5E] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Recipient</th>
              <th className="text-left px-4 py-2 font-medium">Reason</th>
              <th className="text-left px-4 py-2 font-medium">Fails</th>
              <th className="text-left px-4 py-2 font-medium">Blocked since</th>
              <th className="text-left px-4 py-2 font-medium">Dropped</th>
              <th className="text-right px-4 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.recipient} className="border-t border-[#E1DFDD] hover:bg-[#FAFAFA]" data-testid={`suppressed-row-${r.recipient}`}>
                <td className="px-4 py-3 text-[#171717] font-mono text-xs">{r.recipient}</td>
                <td className="px-4 py-3 text-[#5E5E5E] text-xs max-w-[320px] truncate" title={r.last_error || r.reason}>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-2 ${r.source === "manual" ? "bg-[#E1DFDD] text-[#5E5E5E]" : "bg-[#FED9CC] text-[#A4262C]"}`}>
                    {r.source === "manual" ? "manual" : "auto"}
                  </span>
                  {r.reason || "—"}
                </td>
                <td className="px-4 py-3 text-[#5E5E5E] tabular-nums">{r.fail_count ?? 0}</td>
                <td className="px-4 py-3 text-[#5E5E5E] text-xs whitespace-nowrap">{fmt(r.suppressed_at)}</td>
                <td className="px-4 py-3 text-[#5E5E5E] tabular-nums" title="Emails dropped since blocked">{r.blocked_count ?? 0}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUnblock(r.recipient)}
                    data-testid={`unblock-btn-${r.recipient}`}
                    className="h-7 text-xs"
                  >
                    Unblock
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StuckRetryPanel({ rows, onCancel, cancellingId }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      className="mb-5 rounded-md border border-[#F0C040] bg-[#FFF9E8] p-4"
      data-testid="stuck-retry-panel"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-[#8A6116] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#8A6116]">
              {rows.length} email{rows.length === 1 ? "" : "s"} stuck in the retry queue
            </h2>
            <span className="text-[11px] text-[#8A6116]" data-testid="stuck-retry-count">
              auto-retries every few min · cancel to stop the loop
            </span>
          </div>
          <div className="mt-3 space-y-2">
            {rows.map((r) => (
              <div
                key={r.hold_id}
                className="flex items-center justify-between gap-3 rounded border border-[#F0E2B8] bg-white px-3 py-2"
                data-testid={`stuck-retry-row-${r.hold_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-[#171717]">
                    {r.subject || <em className="text-[#5E5E5E]">(no subject)</em>}
                  </div>
                  <div className="truncate text-[11px] text-[#5E5E5E]">
                    {r.mail_from} → {(r.rcpt_tos || []).join(", ")}
                    {" · "}attempt #{(r.retry_count || 0) + 1}/6
                    {r.next_retry_at && <> · next {nextRetryHint(r.next_retry_at)}</>}
                  </div>
                  {r.release_error && (
                    <div className="truncate text-[11px] text-[#A4262C]">
                      {r.release_error_code ? `[${r.release_error_code}] ` : ""}{r.release_error}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancel(r)}
                  disabled={cancellingId === r.hold_id}
                  data-testid={`stuck-cancel-btn-${r.hold_id}`}
                  className="h-8 shrink-0 text-xs text-[#A4262C] hover:bg-[#FED9CC] hover:text-[#A4262C] border-[#A4262C]"
                >
                  <XCircle size={13} className="mr-1.5" />
                  {cancellingId === r.hold_id ? "Cancelling…" : "Cancel"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeldTable({ rows, onRecall, loading, holdWindow }) {
  if (loading && rows.length === 0) {
    return <div className="p-8 text-center text-sm text-[#5E5E5E]">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="p-12 text-center" data-testid="held-empty">
        <MailX size={32} className="mx-auto text-[#5E5E5E] mb-3" strokeWidth={1.4} />
        <p className="text-sm text-[#5E5E5E]">No emails currently held.</p>
        <p className="text-xs text-[#5E5E5E] mt-1">Outbound mail will appear here for 120 seconds before being released.</p>
      </div>
    );
  }
  return (
    <table className="w-full text-sm" data-testid="held-table">
      <thead className="bg-[#FAFAFA] text-[#5E5E5E] text-[11px] uppercase tracking-wider">
        <tr>
          <th className="text-left px-4 py-2 font-medium">From</th>
          <th className="text-left px-4 py-2 font-medium">To</th>
          <th className="text-left px-4 py-2 font-medium">Subject</th>
          <th className="text-left px-4 py-2 font-medium">Mode</th>
          <th className="text-left px-4 py-2 font-medium">Hold remaining</th>
          <th className="text-right px-4 py-2 font-medium">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.hold_id} className="border-t border-[#E1DFDD] hover:bg-[#FAFAFA]" data-testid={`held-row-${r.hold_id}`}>
            <td className="px-4 py-3 text-[#171717] truncate max-w-[200px]">{r.mail_from}</td>
            <td className="px-4 py-3 text-[#5E5E5E] truncate max-w-[200px]">{(r.rcpt_tos || []).join(", ")}</td>
            <td className="px-4 py-3 text-[#171717] truncate max-w-[280px]">{r.subject || <em className="text-[#5E5E5E]">(no subject)</em>}</td>
            <td className="px-4 py-3">
              <span className="text-[10px] uppercase tracking-wider font-medium text-[#5E5E5E]">{r.mode || "—"}</span>
            </td>
            <td className="px-4 py-3"><HoldCountdown releaseAt={r.release_at} holdWindowSeconds={holdWindow} /></td>
            <td className="px-4 py-3 text-right">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRecall(r)}
                data-testid={`recall-btn-${r.hold_id}`}
                className="text-[#A4262C] hover:bg-[#FED9CC] hover:text-[#A4262C] border-[#A4262C]"
              >
                <XCircle size={13} className="mr-1.5" />
                Recall
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecentTable({ rows, loading, onRetry, retryingId, onCancel, cancellingId }) {
  if (loading && rows.length === 0) {
    return <div className="p-8 text-center text-sm text-[#5E5E5E]">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-sm text-[#5E5E5E]">No recent activity yet.</p>
      </div>
    );
  }
  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString();
  };
  return (
    <table className="w-full text-sm" data-testid="recent-table">
      <thead className="bg-[#FAFAFA] text-[#5E5E5E] text-[11px] uppercase tracking-wider">
        <tr>
          <th className="text-left px-4 py-2 font-medium">From</th>
          <th className="text-left px-4 py-2 font-medium">To</th>
          <th className="text-left px-4 py-2 font-medium">Subject</th>
          <th className="text-left px-4 py-2 font-medium">Status</th>
          <th className="text-left px-4 py-2 font-medium">When</th>
          <th className="text-right px-4 py-2 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const rcpt = (r.rcpt_tos && r.rcpt_tos[0]) || "—";
          const extra = (r.rcpt_tos?.length || 1) - 1;
          const isRetrying = retryingId === r.hold_id;
          return (
            <tr key={r.hold_id} className="border-t border-[#E1DFDD] hover:bg-[#FAFAFA]" data-testid={`recent-row-${r.hold_id}`}>
              <td className="px-4 py-3 text-[#171717] truncate max-w-[200px]">{r.mail_from}</td>
              <td className="px-4 py-3 text-[#171717] text-xs font-mono">
                {rcpt}{extra > 0 && <span className="text-[#5E5E5E] ml-1">+{extra}</span>}
              </td>
              <td className="px-4 py-3 text-[#171717] truncate max-w-[280px]">
                {r.subject || <em className="text-[#5E5E5E]">(no subject)</em>}
                {r.mode === "skip" && (
                  <span
                    className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F3F2F1] text-[#5E5E5E] align-middle"
                    title="No signature was injected (reply already contains one)"
                    data-testid={`recent-row-skip-${r.hold_id}`}
                  >
                    no signature
                  </span>
                )}
                {r.mode === "disabled" && (
                  <span
                    className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FFF4CE] text-[#8A6116] align-middle"
                    title="Relay was in pass-through (disabled) mode"
                    data-testid={`recent-row-disabled-${r.hold_id}`}
                  >
                    bypass
                  </span>
                )}
              </td>
              <td className="px-4 py-3 align-top">
                {statusBadge(r.status)}
                {(r.status === "release_failed" || r.status === "release_retrying") && r.release_error && (
                  <div
                    className={`text-[11px] mt-1 break-words max-w-[280px] ${r.status === "release_retrying" ? "text-[#8A6116]" : "text-[#A4262C]"}`}
                    data-testid={`recent-row-error-${r.hold_id}`}
                  >
                    {r.release_error_code ? <span className="font-mono mr-1">[{r.release_error_code}]</span> : null}
                    {r.release_error}
                  </div>
                )}
                {r.status === "release_retrying" && r.next_retry_at && (
                  <div className="text-[10px] text-[#5E5E5E] mt-1" data-testid={`recent-row-nextretry-${r.hold_id}`}>
                    Next attempt {nextRetryHint(r.next_retry_at)} (#{(r.retry_count || 0) + 1}/6)
                  </div>
                )}
                {r.status !== "release_retrying" && r.retry_count > 0 && (
                  <div className="text-[10px] text-[#5E5E5E] mt-1">Retried {r.retry_count}×</div>
                )}
              </td>
              <td className="px-4 py-3 text-[#5E5E5E] text-xs whitespace-nowrap">
                {fmt(r.released_at || r.recalled_at || r.created_at)}
              </td>
              <td className="px-4 py-3 text-right">
                {r.status === "release_failed" && onRetry && r.mode !== "skip" && r.mode !== "disabled" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRetry(r)}
                    disabled={isRetrying}
                    data-testid={`retry-btn-${r.hold_id}`}
                    className="h-7 text-xs mr-1"
                  >
                    <RefreshCw size={11} className={`mr-1 ${isRetrying ? "animate-spin" : ""}`} />
                    {isRetrying ? "Retrying…" : "Retry"}
                  </Button>
                )}
                {r.status === "release_failed" && onCancel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancel(r)}
                    disabled={cancellingId === r.hold_id}
                    data-testid={`dismiss-btn-${r.hold_id}`}
                    className="h-7 text-xs text-[#5E5E5E] hover:text-[#A4262C]"
                  >
                    <XCircle size={11} className="mr-1" />
                    {cancellingId === r.hold_id ? "Dismissing…" : "Dismiss"}
                  </Button>
                )}
                {r.status === "release_retrying" && onCancel && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCancel(r)}
                    disabled={cancellingId === r.hold_id}
                    data-testid={`cancel-retry-btn-${r.hold_id}`}
                    className="h-7 text-xs text-[#A4262C] hover:bg-[#FED9CC] hover:text-[#A4262C] border-[#A4262C]"
                  >
                    <XCircle size={11} className="mr-1" />
                    {cancellingId === r.hold_id ? "Cancelling…" : "Cancel"}
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
